// Phase-5 PTT — Unit 3: atomic, fail-closed voice {domain, agent} binding.
//
// The domain/agent pair-drift bug class (project_domain_agent_pair_drift): activeDomain
// and activeAgent live as TWO separate useState values in App.tsx (102/104), set across
// async boundaries (e.g. setActiveDomain(d); setActiveAgent(null) at 442-443). usePTT
// then consumed them with a FAIL-OPEN default — `activeAgent || 'SPENCER'` — which on a
// non-PERSONAL domain silently binds a MISMATCHED pair (e.g. {TRADING, SPENCER}); the
// server then rejects it (validate_domain_agent), so the user taps a Trading agent and
// gets a confusing failure instead of routing to the agent they intended.
//
// This resolver is the fail-closed consumer guard half of the locked fix: it takes the
// loose (domain, agent) and returns ONE canonical, normalized, validated atomic blob —
// or a fail-closed result. It NEVER substitutes a default agent (no SPENCER fallback).
//
// Membership ("is AGENT a member of DOMAIN?") is deliberately NOT replicated here. The
// server registry (core/agent_registry.validate_domain_agent) is the single source of
// truth and already fails closed with a precise message; the client roster (lib/agents.ts)
// is a separate artifact that can drift from it and does NOT even contain HOMEPAGE/REXY
// (the homepage front door). Re-deriving membership client-side would reintroduce the very
// drift class this unit closes. We enforce COMPLETENESS + ATOMICITY here; the server
// enforces MEMBERSHIP. ("Code enforces, agents observe" — the server is the wall.)

export interface VoiceBindingInput {
  domain: string;
  agent: string | null;
}

export type VoiceBindingResult =
  | { ok: true; domain: string; agent: string }
  | { ok: false; reason: 'no_domain' | 'no_agent' };

/**
 * Resolve a loose (domain, agent) into a single canonical voice binding.
 * - Normalizes to the registry's UPPERCASE/trimmed form.
 * - Fails CLOSED (never substitutes a default) if either half is missing — so an
 *   incomplete pair can never bind a mismatched agent. The caller surfaces the
 *   failure and does NOT open a voice session.
 */
export function resolveVoiceBinding(input: VoiceBindingInput): VoiceBindingResult {
  const domain = (input.domain || '').trim().toUpperCase();
  const agent = (input.agent || '').trim().toUpperCase();
  if (!domain) return { ok: false, reason: 'no_domain' };
  if (!agent) return { ok: false, reason: 'no_agent' };
  return { ok: true, domain, agent };
}
